import { Link as RouterLink } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Button, Container, Grid, Stack, Typography, Card, CardContent } from '@mui/material';

// third party
import { motion } from 'framer-motion';

// project imports

// icons
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StorageIcon from '@mui/icons-material/Storage';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HubIcon from '@mui/icons-material/Hub';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SecurityIcon from '@mui/icons-material/Security';

// ==============================|| LANDING - HEADER PAGE ||============================== //

const HeaderSection = ({ onExperienceClick }) => {
    const theme = useTheme();

    const handleExperienceClick = () => {
        if (onExperienceClick) {
            onExperienceClick();
        }
    };

    const features = [
        {
            icon: <CloudUploadIcon sx={{ fontSize: 40, color: '#3B82F6' }} />,
            title: 'Multi-Cloud Storage',
            description: 'S3, MinIO, GCS, Azure'
        },
        {
            icon: <HubIcon sx={{ fontSize: 40, color: '#8B5CF6' }} />,
            title: 'JupyterHub',
            description: 'GPU-enabled computing'
        },
        {
            icon: <AnalyticsIcon sx={{ fontSize: 40, color: '#10B981' }} />,
            title: 'Airflow Orchestration',
            description: 'Workflow automation'
        },
        {
            icon: <StorageIcon sx={{ fontSize: 40, color: '#F59E0B' }} />,
            title: 'MongoDB',
            description: 'Document storage'
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40, color: '#EF4444' }} />,
            title: 'API Gateway',
            description: 'Rate limiting & Security'
        }
    ];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background:
                    theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)'
                        : 'linear-gradient(135deg, #F8FAFC 0%, #E0E7FF 100%)',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-20%',
                    width: '70%',
                    height: '150%',
                    background:
                        theme.palette.mode === 'dark'
                            ? 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)'
                            : 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'rotate(45deg)'
                }
            }}
        >
            <Container sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <motion.div
                            initial={{ opacity: 0, translateX: -50 }}
                            animate={{ opacity: 1, translateX: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Stack spacing={4}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontWeight: 600,
                                            mb: 2,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1.5
                                        }}
                                    >
                                        Enterprise Data Platform
                                    </Typography>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                                            fontWeight: 800,
                                            lineHeight: 1.2,
                                            mb: 3,
                                            background:
                                                theme.palette.mode === 'dark'
                                                    ? 'linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%)'
                                                    : 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}
                                    >
                                        DataHub
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.6,
                                            mb: 4,
                                            maxWidth: 600
                                        }}
                                    >
                                        Nền tảng dữ liệu toàn diện cho doanh nghiệp với khả năng lưu trữ đa dạng, tính toán mạnh mẽ và quản
                                        lý quy trình thông minh.
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={2}>
                                    <Button
                                        component={RouterLink}
                                        to="/login"
                                        size="large"
                                        variant="contained"
                                        startIcon={<PlayArrowIcon />}
                                        sx={{
                                            py: 1.5,
                                            px: 4,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                                            boxShadow: '0 4px 14px 0 rgba(59,130,246,0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 6px 20px 0 rgba(59,130,246,0.4)'
                                            }
                                        }}
                                    >
                                        Bắt đầu ngay
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleExperienceClick}
                                        size="large"
                                        sx={{
                                            py: 1.5,
                                            px: 4,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                            '&:hover': {
                                                borderColor: theme.palette.primary.dark,
                                                backgroundColor: theme.palette.primary.main + '10'
                                            }
                                        }}
                                    >
                                        Khám phá tính năng
                                    </Button>
                                </Stack>

                                <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
                                    <Box>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                            1000+
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Doanh nghiệp tin dùng
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                            10TB+
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Dữ liệu được xử lý
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                            99.9%
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Uptime
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </motion.div>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        <motion.div
                            initial={{ opacity: 0, translateX: 50 }}
                            animate={{ opacity: 1, translateX: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <Grid container spacing={3}>
                                {features.map((feature, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <motion.div
                                            initial={{ opacity: 0, translateY: 20 }}
                                            animate={{ opacity: 1, translateY: 0 }}
                                            transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                                        >
                                            <Card
                                                sx={{
                                                    height: '100%',
                                                    background:
                                                        theme.palette.mode === 'dark'
                                                            ? 'rgba(30, 41, 59, 0.5)'
                                                            : 'rgba(255, 255, 255, 0.8)',
                                                    backdropFilter: 'blur(10px)',
                                                    border: '1px solid',
                                                    borderColor:
                                                        theme.palette.mode === 'dark'
                                                            ? 'rgba(148, 163, 184, 0.1)'
                                                            : 'rgba(148, 163, 184, 0.2)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-5px)',
                                                        boxShadow:
                                                            theme.palette.mode === 'dark'
                                                                ? '0 10px 30px rgba(0,0,0,0.3)'
                                                                : '0 10px 30px rgba(0,0,0,0.1)',
                                                        borderColor: theme.palette.primary.main
                                                    }
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                                                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {feature.description}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            {/* Animated background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -100,
                    left: -100,
                    width: 300,
                    height: 300,
                    borderRadius: '50%',
                    background:
                        theme.palette.mode === 'dark'
                            ? 'radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%)'
                            : 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
                    animation: 'float 6s ease-in-out infinite',
                    '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-20px)' }
                    }
                }}
            />
        </Box>
    );
};

export default HeaderSection;
